import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isAfter, parseISO, startOfWeek } from 'date-fns';
import { GetTimeSlotsPayload, TimeSlotsState } from './timeSlotsTypes';
import { Slots } from '../../services/Appointment/model';
import {
  getFormattedTimeSlots,
  getSortedTimeSlots,
  ServiceLocation,
  startOfWeekDayToNumber,
} from '../../helpers';
import { services } from '../../context';
import { getInitialSessionOnClientEntry } from '../../helpers/sessionHelper';

const WEEK_PERIOD = 7;

const initialState = (): TimeSlotsState => {
  const restoredState = getInitialSessionOnClientEntry();
  return {
    from: restoredState.from || new Date().toISOString(),
    prevEnabled: false,
    availableTimeSlots: {},
    replacementServiceAvailableTimeSlots: {},
  };
};

async function fetchAndAggregateTimeSlots(
  zipCode: string,
  serviceId: string,
  location: ServiceLocation,
  storeId: string,
  from: string,
  serviceCode: string,
  weekStartsOn: 0 | 1,
): Promise<{ allTimeSlots: Slots[]; error: boolean }> {
  const requests = Array.from({ length: 4 }, (_, i) => {
    const weekStart = startOfWeek(parseISO(from), { weekStartsOn });
    weekStart.setDate(weekStart.getDate() + i * 7);
    return services.appointment.getAvailableTimeslots(
      zipCode,
      serviceId,
      location,
      storeId,
      weekStart.toISOString(),
      serviceCode,
      WEEK_PERIOD,
    );
  });

  const results = await Promise.all(requests);

  const allTimeSlots: Slots[] = [];
  let error = false;
  for (const res of results) {
    if (res.error) {
      error = true;
    }
    if (res.result && Array.isArray(res.result.timeSlots)) {
      allTimeSlots.push(...res.result.timeSlots);
    }
  }

  return { allTimeSlots, error };
}

export const getTimeSlots = createAsyncThunk(
  'timeSlots/getTimeSlots',
  async ({
    zipCode,
    serviceId,
    location,
    storeId,
    from,
    serviceCode,
    isReplacementService,
  }: {
    zipCode: string;
    serviceId: string;
    location: ServiceLocation;
    storeId: string;
    from: string;
    serviceCode: string;
    isReplacementService?: boolean;
  }) => {
    const weekStartsOn = startOfWeekDayToNumber(
      CSS_CONFIG.FORMATS.firstDayOfWeek,
    );
    const firstDayOfWeek = startOfWeek(parseISO(from), { weekStartsOn });
    const prevEnabled = isAfter(firstDayOfWeek, new Date());
    let timeSlots = [];
    const { splitTimeSlotRequest } = CSS_CONFIG.SERVICES;

    if (splitTimeSlotRequest.includes(serviceId)) {
      const { allTimeSlots, error } = await fetchAndAggregateTimeSlots(
        zipCode,
        serviceId,
        location,
        storeId,
        from,
        serviceCode,
        weekStartsOn,
      );
      timeSlots = allTimeSlots;
      if (error) {
        return Promise.reject();
      }
    } else {
      const { result, error } =
        await services.appointment.getAvailableTimeslots(
          zipCode,
          serviceId,
          location,
          storeId,
          firstDayOfWeek.toISOString(),
          serviceCode,
        );
      timeSlots = result.timeSlots;
      if (error) {
        return Promise.reject();
      }
    }
    const replacementServiceReformattedDates = getFormattedTimeSlots(timeSlots);
    const availableTimeSlots: Slots[] = getSortedTimeSlots(
      replacementServiceReformattedDates,
    );
    return {
      storeId,
      from: firstDayOfWeek.toISOString(),
      prevEnabled,
      availableTimeSlots,
      isReplacementService,
    };
  },
);

const timeSlotsSlice = createSlice({
  name: 'timeSlots',
  initialState: initialState(),
  reducers: {
    clearAvailableTimeSlots(state) {
      state.availableTimeSlots = {};
      state.replacementServiceAvailableTimeSlots = {};
    },
    resetTimeSlotsFromDate(state) {
      state.from = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getTimeSlots.fulfilled,
      (state, action: PayloadAction<GetTimeSlotsPayload>) => {
        state.from = action.payload.from;
        state.prevEnabled = action.payload.prevEnabled;
        if (action.payload.isReplacementService) {
          state.replacementServiceAvailableTimeSlots = {
            [action.payload.storeId]: action.payload.availableTimeSlots,
          };
        } else {
          state.availableTimeSlots = {
            [action.payload.storeId]: action.payload.availableTimeSlots,
          };
        }
      },
    );
  },
});

export const { clearAvailableTimeSlots, resetTimeSlotsFromDate } =
  timeSlotsSlice.actions;

export default timeSlotsSlice.reducer;
